import { EyeIcon, StarIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { ReactNode, createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ListFieldsKeys, ListFieldsRecordType, emailsSender, paramsListName, siteUrl } from "src/api/config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import { getNavigation } from "src/components/Sidebar";
import GetNotificationTable from "src/components/tables/notificationTables/GetNotificationTable";
import { NotificationIcon, NotificationSmallIcon } from "src/components/ui-icons/card";
import TGButton from "src/components/ui/TGButton";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { NavItemType } from "src/layouts/DashboardLayout";
import { getItems } from "src/modules/spItems/actions";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import SelectDocuments, { NotifyMyDocumentsType } from "./SelectDocuments";
import SelectTests from "./SelectTests";
import SelectReviewDate from "./SelectReviewDate";
import SelectComment from "./SelectComment";
import SelectEmployees, { NotifyMyEmployeesType } from "./SelectEmployees";

import { addItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { getNotificationsInfo } from "src/modules/acadia/notifications/actions";
import { selectCount } from "src/modules/spItems/selectors/selectCount";
import SendNotificationTable from "src/components/tables/notificationTables/SendNotificationTable";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { useFiltersContext } from "src/contexts/FilterContext";
import {
  checkListsOWDListName,
  getNotificationsListName,
  notificationStatusesListName,
  notificationIdsListName,
  notificationTests,
  notificationTestsAnswers,
  notificationTestsQuestion,
  notificationsListName,
  sendNotificationsListName,
} from "src/api/config/notifications.config";
import { ITesting } from "../components/Tests/types";

import { selectCurrentUser } from "src/modules/users/selector";
import { emailAPI, getParamsByKey } from "src/utils/api.utils";
import { removeInvisibleChar } from "src/utils/string.utils";
import { emailQueueListName } from "src/api/config/email.config";
import { getDiffDays } from "src/utils/date.utils";
import { addAnalysis } from "src/modules/acadia/constants";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { cloneDeep } from "lodash-es";
import Loading from "src/components/Loading";
import { autoCloseModal, mode } from "src/constants";
import { selectJustUpdatedItem } from "src/modules/spItems/selectors";
import { notificationStatuses } from "src/api/config/systemSwitch";
import { isMobile } from "src/utils/device";
import NotificationPageMobile from "./notificationPageMobile";

const { sendEmail, createEmailBodyByTemplate } = emailAPI();

function getCountNotificationIDs(items: ListFieldsRecordType[typeof notificationsListName][]) {
  const uniqueNotifications = new Set();

  items.forEach((item) => {
    if (!item.Title.includes("Отклонено")) {
      uniqueNotifications.add(+item.NotificationID.get_lookupId());
    }
  });

  return uniqueNotifications.size;
}

/** Compare calendar day in UTC so it matches getDiffDays / moment.utc(reviewDate) and avoids duplicate emails when “today” differs by timezone. */
function isSameUtcCalendarDay(reviewDate: Date | string | number, sendAt: Date): boolean {
  return moment.utc(reviewDate).isSame(moment.utc(sendAt), "day");
}

interface StatusContextType {
  status: Record<ListFieldsKeys, number> | {};
  changeStatus: (value: number) => void;
}
const StatusContext = createContext<StatusContextType | null>(null);

export const useStatusContext = (): StatusContextType => {
  const context = useContext(StatusContext);

  if (!context) {
    throw new Error("useStatusContext должен использоваться внутри StatusProvider");
  }

  return context;
};

export function StatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState(0);

  function changeStatus(value: number) {
    setStatus(value);
  }

  const contextValue: StatusContextType = {
    status,
    changeStatus,
  };

  return <StatusContext.Provider value={contextValue}>{children}</StatusContext.Provider>;
}

const NotificationPageDesktop = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const [query, setQuery] = useSearchParams();

  const updateNotification = useAppSelector((state) => selectJustUpdatedItem(state, notificationsListName));

  const [activeNotifyList, setActiveNotifyList] = useState<"get" | "send">(
    getLocalItem("notification_active") ?? "get",
  );

  const notifyCardQuery = useMemo(() => query.has("notificationCard"), [query]);

  const toggleNotify = useCallback((action: "get" | "send") => {
    setActiveNotifyList(action);
    setLocalItem("notification_active", action);
  }, []);

  useEffect(() => {
    dispatch(getItems({ listName: notificationStatusesListName }));
  }, [dispatch]);

  useEffect(() => {
    if (currentUser.email) {
      if (!getLocalItem("notification_active")) setLocalItem("notification_active", "get");

      dispatch(getItems({ listName: checkListsOWDListName }));

      dispatch(
        getNotificationsInfo({
          activeNotifyList,
          listName: notificationsListName,
        }),
      );
    }
  }, [activeNotifyList, dispatch, notifyCardQuery, currentUser.email, updateNotification]);

  return (
    <>
      <FlexRow className="px-7.5 h-[76px] border-b items-center shrink-0">
        <NotificationPageBreadcrumbLayout activeNotifyList={activeNotifyList} changeActive={toggleNotify} />
      </FlexRow>
      <div className="p-7.5">{activeNotifyList === "get" ? <GetNotificationTable /> : <SendNotificationTable />}</div>
    </>
  );
};

const NotificationPage = () => {
  const [mobileView, setMobileView] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => {
      setMobileView(isMobile());
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return mobileView ? <NotificationPageMobile /> : <NotificationPageDesktop />;
};

export default NotificationPage;

type SubmitType = {
  documents: Map<number, NotifyMyDocumentsType>;
  reviewDate: Date;
  comment: string;
  employees: Map<number, NotifyMyEmployeesType>;
  test: ITesting;
};

export const NotificationPageBreadcrumbLayout = memo(
  ({
    activeNotifyList,
    changeActive,
  }: {
    activeNotifyList: "get" | "send";
    changeActive(active: "get" | "send"): void;
  }) => {
    const { id: queryId } = useParams();
    const getNotifications = useAppSelector((state) => selectCount(state, getNotificationsListName));
    const sendNotifications = useAppSelector((state) => selectCount(state, sendNotificationsListName));
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const navItem: NavItemType | undefined = useMemo(
      () => getNavigation(queryId).find((n) => n.url === pathname),
      [queryId, pathname],
    );

    if (!navItem) {
      navigate("/");
      return null;
    }

    return (
      <nav className="flex justify-between items-center w-full" aria-label="Breadcrumb">
        <FlexCol className="grow">
          <ol className="inline-flex">
            <li className="inline-flex items-center" aria-current="page">
              <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
                {navItem.icon ?? null}
                <span>{navItem.name}</span>
              </div>
              <span className="px-3 text-body-color">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                    fill={"#637381"}
                  />
                </svg>
              </span>
            </li>
            <li
              className="flex flex-row items-center justify-between font-medium divide-x  divide-[#637381]"
              aria-current="page"
            >
              <FlexCol className="pr-3">
                <button
                  onClick={() => changeActive("get")}
                  className={twMerge(activeNotifyList === "get" ? "text-darkblue" : "text-[#637381]")}
                >
                  Полученные · {getNotifications}
                </button>
              </FlexCol>
              <FlexCol className="pl-3">
                <button
                  onClick={() => changeActive("send")}
                  className={twMerge(activeNotifyList === "send" ? "text-darkblue" : "text-[#637381]")}
                >
                  Отправленные · {sendNotifications}
                </button>
              </FlexCol>
            </li>
          </ol>
        </FlexCol>
        <FlexCol>
          <FlexRow className="gap-x-2.5">
            <NotificationRegistryButton />
          </FlexRow>
        </FlexCol>
      </nav>
    );
  },
);

function NotificationRegistryButton() {
  const navigate = useNavigate();

  const openDialog = async () => {
    navigate("/dashboard/1/reports/4");
  };

  return (
    <>
      <TGButton onClick={openDialog}>Реестр уведомлений</TGButton>
    </>
  );
}

type NotifyType = "audit" | "owd";

export type UserType = { name: string; avatar: string };

export function NotificationSendButton({
  isIconOnly = false,
  changeActive,
  read,
  title = "Отправить уведомление",
  zIndex = 50,
  initialSubmit = {
    documents: new Map(),
    reviewDate: moment(new Date()).add(30, "day").toDate(),
    comment: "",
    employees: new Map(),
    test: {
      Questions: [],
      IsQuestionFormOpen: false,
      MinCorrectAnswersCount: 1,
    },
  },
}: {
  isIconOnly?: boolean;
  changeActive(active: "get" | "send"): void;
  read?: Record<any, boolean>;
  title?: string;
  zIndex?: number;
  initialSubmit?: SubmitType;
}) {
  const [submitState, setSubmitState] = useState<SubmitType>(cloneDeep(initialSubmit));
  const [submitLoading, setSubmitLoading] = useState(false);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const { changeStatus } = useStatusContext();
  const { resetFilters } = useFiltersContext();
  const [open, setOpen] = useState(false);
  const [openDrawerAudit, setOpenDrawerAudit] = useState(false);

  const [openDrawerOWD, setOpenDrawerOWD] = useState(false);
  const [openSubmitNotificationModal, setOpenSubmitNotificationModal] = useState(false);

  const [typeOfNotify, setTypeOfNotify] = useState<NotifyType>("audit");

  useEffect(() => {
    if (!openDrawerAudit) {
      localStorage.setItem("questions", "");
      localStorage.setItem("minCorrectAnswersCount", "1");
    }
  }, [openDrawerAudit]);

  function chooseNotify(type: NotifyType) {
    setTypeOfNotify(type);
  }

  const closeDialog = () => {
    setOpen(false);
  };

  const toggleSubmitNotificationModal = (state: boolean) => {
    setOpenSubmitNotificationModal(state);
  };

  const clearSubmit = () => {
    setSubmitState(cloneDeep(initialSubmit));
    resetFilters();
  };

  const closeAllModals = () => {
    setOpen(false);
    setOpenDrawerAudit(false);
    setOpenDrawerOWD(false);
    setOpenSubmitNotificationModal(false);
    clearSubmit();
  };

  const isButtonDisabledMemoized = useMemo(() => {
    return !!submitState.documents.size && !!submitState.employees.size && !!submitState.reviewDate;
  }, [submitState, submitState.documents]);

  async function handleAuditSubmit() {
    try {
      setSubmitLoading(true);

      ///////////////////////////////////////////////
      const questions = submitState.test.Questions;
      const questionsID = [];
      let answersID = [];
      const minCorrectAnswerCount = submitState.test.MinCorrectAnswersCount;

      const notificationID = (await addItemRequest({
        listName: notificationIdsListName,
        body: { Title: "Ознакомление" },
        updateReducer: false,
      })) as number;

      const STRAPI_TITLE_MAX_LENGTH = 255;

      if (questions.length) {
        for (const question of questions) {
          for (const answer of question.Answers) {
            const ID = await addItemRequest({
              listName: notificationTestsAnswers,
              body: {
                ...answer,
                Title:
                  answer.Description.length > STRAPI_TITLE_MAX_LENGTH
                    ? answer.Description.slice(0, STRAPI_TITLE_MAX_LENGTH)
                    : answer.Description,
              },
              updateReducer: false,
            });
            answersID.push(ID);
          }
          const idQuestion = await addItemRequest({
            listName: notificationTestsQuestion,
            body: {
              ...question,
              Title:
                question.Description.length > STRAPI_TITLE_MAX_LENGTH
                  ? question.Description.slice(0, STRAPI_TITLE_MAX_LENGTH)
                  : question.Description,
              Answers: answersID,
            },
            updateReducer: false,
          });
          questionsID.push(idQuestion);
          answersID.length = 0;
        }
      }

      const testID: unknown = questions.length
        ? await addItemRequest({
            listName: notificationTests,
            body: {
              Title: `Тест № ${notificationID}`,
              Questions: questionsID,
              MinCorrectAnswerCount: minCorrectAnswerCount,
            },
            updateReducer: false,
          })
        : null;

      ///////////////////////////////////////////////

      const notificationPromises = [];
      const emailPromises = [];

      for (const [keyEmp, emp] of submitState.employees) {
        const notifyMap = new Map();

        for (const [key, doc] of submitState.documents) {
          const submitObject: Partial<Record<keyof ListFieldsRecordType[typeof notificationsListName], any>> = {
            Title: "Уведомление " + notificationID,
            NotificationID: notificationID,
            Employee: emp.user.email.toLowerCase(),
            EmployeeEmail: emp.user.email.toLowerCase(),
            Document: doc.id,
            DocumentCode: doc.code.code,
            ReviewDate: submitState.reviewDate,
            AuthorComment: submitState.comment,
            NotificationType: 5,
            AuthorEmail: currentUser.email.toLowerCase(),
            // Author: currentUser.id,
          };

          if (testID) {
            submitObject.Test = testID as number;
            submitObject.NotificationType = 4;
          }

          const notificationPromise = addItemRequest({
            listName: notificationsListName,
            body: submitObject,
            updateReducer: false,
          }).then((id: unknown) => {
            const notificationsListID = id as number;
            if (notificationsListID > 0) {
              if (notifyMap.has(doc)) {
                const arrayBody = notifyMap.get(doc);
                notifyMap.set(doc, [...arrayBody, notificationsListID]);
              } else {
                notifyMap.set(doc, [notificationsListID]);
              }
            }
          });

          notificationPromises.push(notificationPromise);
        }

        await Promise.all(notificationPromises);

        if (emailsSender) {
          const tempEmailBodyObject = {
            subject: `Мои документы | Ознакомление с документами`,
            user: {
              name: emp.user.name + ` (${emp.user.personalNumber || "Нет номера"})`,
              email: emp.user.email,
            },
            document: {
              title: `Прочитайте следующий (-е) документ (-ы)${
                questions.length ? ", пройдите тестирование" : ""
              } и подтвердите факт ознакомления с документом (-ами)`,
              lists: [...notifyMap.entries()].map(([doc, id]) => ({
                link: `${siteUrl}/dashboard/1/notification?notificationCard=${id}`, //* если что изменить на динамический
                title: doc.code.name,
              })),
            },
            periodDate: {
              title: "Срок ознакомления",
              date: submitState.reviewDate,
            },
            header: `Supply Portal -> Мои документы | Ознакомление с документами`,
          };

          const emailBody = createEmailBodyByTemplate(tempEmailBodyObject);

          const { items } = await getItemsRequest({
            listName: paramsListName,
            filters: { Key: "audit" },
          });

          const stringWithoutInvisibleChar = removeInvisibleChar(items[0].Value); // minutes

          const diffReviewDays = getDiffDays(moment.utc(submitState.reviewDate).toDate(), moment.utc().toDate());

          const repeatDays = +stringWithoutInvisibleChar / 60 / 24;

          const isLessThanCurrentDate = diffReviewDays < repeatDays;

          const emailRequestBody: Partial<ListFieldsRecordType[typeof emailQueueListName]> = {
            Body: emailBody,
            DateToBeSentAt: new Date(),
            Title: `Ознакомление с документами`,
            Subject: `Мои документы | Ознакомление с документами`,
            NotificationID: notificationID,
            Recepients: emp.user.email.toLowerCase(),
            RepeatAt: 0,
            RepeatTimes: 0,
          };

          emailPromises.push(
            sendEmail(emailRequestBody).then(async () => {
              if (items[0] && items[0].Value) {
                if (
                  !isSameUtcCalendarDay(submitState.reviewDate, emailRequestBody.DateToBeSentAt as Date) &&
                  !isLessThanCurrentDate // только если дедлайн дальше периода повтора
                ) {
                  const emailBody = createEmailBodyByTemplate({
                    ...tempEmailBodyObject,
                    periodDate: {
                      ...tempEmailBodyObject.periodDate,
                      expired: true,
                    },
                  });

                  emailRequestBody.Body = emailBody;
                  emailRequestBody.DateToBeSentAt = submitState.reviewDate;
                  emailRequestBody.RepeatAt = +stringWithoutInvisibleChar;
                  emailRequestBody.RepeatTimes = 100000;

                  await sendEmail(emailRequestBody);
                }
              }
            }),
          );
        }
      }

      await Promise.all(emailPromises);

      const firstDoc = [...submitState.documents.values()][0];
      addAnalysis(currentUser, firstDoc.id, firstDoc.code.code, "isActive", "OWD");

      setOpenSubmitNotificationModal(true);
      dispatch(
        getNotificationsInfo({
          activeNotifyList: "send",
          listName: notificationsListName,
        }),
      );
      changeActive("send");
    } catch (error) {
      console.log("error => handleAuditSubmit", error);
      throw new Error("Function not implemented.");
    } finally {
      changeStatus(Date.now());
      setSubmitLoading(false);
    }
  }

  async function handleOWDSubmit() {
    try {
      setSubmitLoading(true);

      const notificationID = (await addItemRequest({
        listName: notificationIdsListName,
        body: { Title: "OWD" },
        updateReducer: false,
      })) as number;

      const notificationsPromises = [];
      const emailPromises = [];
      const notifyMaps = new Map<string, Map<NotifyMyDocumentsType, number[]>>();

      for (const [keyEmp, emp] of submitState.employees) {
        const notifyMap = new Map<NotifyMyDocumentsType, number[]>();
        notifyMaps.set(emp.user.email.toLowerCase(), notifyMap);

        for (const [key, doc] of submitState.documents) {
          const submitObject = {
            Title: "OWD " + notificationID,
            NotificationID: notificationID,
            Employee: emp.user.email.toLowerCase(),
            EmployeeEmail: emp.user.email.toLowerCase(),
            Document: doc.id,
            DocumentCode: doc.code.code,
            ReviewDate: submitState.reviewDate,
            AuthorComment: submitState.comment,
            NotificationType: 6,
            AuthorEmail: currentUser.email.toLowerCase(),
            // Author: currentUser.id,
          };

          const notificationPromise = addItemRequest({
            listName: notificationsListName,
            body: submitObject,
            updateReducer: false,
          }).then((id: unknown) => {
            const notificationsListID = id as number;

            if (notificationsListID > 0) {
              if (notifyMap.has(doc)) {
                const arrayBody = notifyMap.get(doc) || [];
                notifyMap.set(doc, [...arrayBody, notificationsListID]);
              } else {
                notifyMap.set(doc, [notificationsListID]);
              }
            }
          });

          notificationsPromises.push(notificationPromise);
        }
      }

      await Promise.all(notificationsPromises);

      if (emailsSender) {
        for (const [keyEmp, emp] of submitState.employees) {
          const notifyMap = notifyMaps.get(emp.user.email.toLowerCase());
          if (notifyMap) {
            const tempEmailBodyObject = {
              subject: `Мои Документы | Проведение OWD`,
              user: {
                name: emp.user.name + ` (${emp.user.personalNumber || "Нет номера"})`,
                email: emp.user.email,
              },
              document: {
                title: `Проведите OWD по следующему (-им) документу (-ам)`,
                lists: [...notifyMap.entries()].map(([doc, ids]) => ({
                  link: `${siteUrl}/dashboard/1/notification?notificationCard=${ids.join(
                    //* если что изменить на динамический
                    ",",
                  )}`,
                  title: doc.code.name,
                })),
              },
              periodDate: {
                title: "Срок проведения OWD",
                date: submitState.reviewDate,
              },
              header: "Supply Portal -> Мои Документы | Проведение OWD",
            };

            const emailBody = createEmailBodyByTemplate(tempEmailBodyObject);

            const { items } = await getItemsRequest({
              listName: paramsListName,
              filters: { Key: "owd" },
            });

            const stringWithoutInvisibleChar = removeInvisibleChar(items[0].Value); // minutes

            const diffReviewDays = getDiffDays(moment.utc(submitState.reviewDate).toDate(), moment.utc().toDate());

            const repeatDays = +stringWithoutInvisibleChar / 60 / 24;

            const isLessThanCurrentDate = diffReviewDays < repeatDays;

            const emailRequestBody: Partial<ListFieldsRecordType[typeof emailQueueListName]> = {
              Body: emailBody,
              DateToBeSentAt: new Date(),
              Title: "OWD",
              Subject: "Мои Документы | Проведение OWD",
              NotificationID: notificationID,
              Recepients: emp.user.email.toLowerCase(),
              RepeatAt: 0,
              RepeatTimes: 0,
            };

            const emailPromise = sendEmail(emailRequestBody).then(async () => {
              if (items[0] && items[0].Value) {
                if (
                  !isSameUtcCalendarDay(submitState.reviewDate, emailRequestBody.DateToBeSentAt as Date) &&
                  !isLessThanCurrentDate
                ) {
                  const emailBody = createEmailBodyByTemplate({
                    ...tempEmailBodyObject,
                    periodDate: {
                      ...tempEmailBodyObject.periodDate,
                      expired: true,
                    },
                  });

                  emailRequestBody.Body = emailBody;
                  emailRequestBody.DateToBeSentAt = submitState.reviewDate;
                  emailRequestBody.RepeatAt = +stringWithoutInvisibleChar;
                  emailRequestBody.RepeatTimes = 100000;

                  await sendEmail(emailRequestBody);
                }
              }
            });

            emailPromises.push(emailPromise);
          }
        }

        await Promise.all(emailPromises);
      }

      const firstDoc = [...submitState.documents.values()][0];

      addAnalysis(currentUser, firstDoc.id, firstDoc.code.code, "isActive", "OWD");

      setOpenSubmitNotificationModal(true);
      changeActive("send");

      dispatch(
        getNotificationsInfo({
          activeNotifyList: "send",
          listName: notificationsListName,
        }),
      );
    } catch (error) {
      console.log("error => handleOWDSubmit", error);
      throw new Error("Function not implemented.");
    } finally {
      changeStatus(Date.now());
      setSubmitLoading(false);
    }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        let dateString = "";

        if (typeOfNotify === "audit") {
          dateString = await getParamsByKey("auditDateDefault");
        } else if (typeOfNotify === "owd") {
          dateString = await getParamsByKey("owdDateDefault");
        }

        if (dateString && +dateString) {
          const momentDate = moment();

          const reviewDateWithAddDays = momentDate.add(+dateString, "day").toDate();

          setSubmitState((prev) => ({
            ...prev,
            reviewDate: reviewDateWithAddDays,
          }));
        }
      } catch (error) {
        console.error("error", error);
      }
    }

    fetchData();
  }, [openDrawerAudit, openDrawerOWD, typeOfNotify]);

  return (
    <>
      {isIconOnly ? (
        <button onClick={() => setOpen(!open)}>
          <NotificationIcon />
        </button>
      ) : (
        <TGButton icon={<NotificationSmallIcon />} onClick={() => setOpen(!open)} color="bluedark">
          {title}
        </TGButton>
      )}

      <Modal open={open} zIndex={zIndex} toggleDialog={closeDialog}>
        <FlexCol className="shrink">
          <header className="px-10 pb-4">
            <h2>Отправка уведомления</h2>
          </header>

          <ChooseTypeOfNotifyComponent type={typeOfNotify} onChange={chooseNotify} />

          <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
            <TGButton onClick={closeDialog}>Отмена</TGButton>
            <TGButton
              onClick={() => (typeOfNotify === "audit" ? setOpenDrawerAudit(true) : setOpenDrawerOWD(true))}
              color="bluedark"
            >
              Продолжить
            </TGButton>
          </footer>
        </FlexCol>
        {typeOfNotify === "audit" ? (
          <DrawerComponent
            zIndex={zIndex}
            open={openDrawerAudit}
            toggleDrawer={() => setOpenDrawerAudit((prev) => !prev)}
          >
            <header className="flex px-10 pt-10 pb-8 border-b shrink-0">
              <h1 className="text-black">Распространение ознакомления</h1>
            </header>
            <FlexCol className="divide-y grow">
              <SelectDocuments
                zIndex={zIndex}
                initial={submitState.documents}
                read={read?.documents}
                onResult={(items: Map<number, NotifyMyDocumentsType>) => {
                  setSubmitState((prev) => ({ ...prev, documents: items }));
                }}
              />
              <SelectEmployees
                zIndex={zIndex}
                title="Сотрудники"
                initial={submitState.employees}
                onResult={(items: Map<number, NotifyMyEmployeesType>) => {
                  setSubmitState((prev) => ({ ...prev, employees: items }));
                }}
              />

              <SelectTests
                zIndex={zIndex}
                onResult={(data: ITesting) => {
                  setSubmitState((prev) => ({ ...prev, test: data }));
                }}
              />

              <SelectReviewDate
                zIndex={zIndex}
                title="Срок ознакомления"
                modalTitle="Укажите срок ознакомления"
                modalDescription="Срок ознакомления"
                initial={submitState.reviewDate}
                onResult={(date: Date) => {
                  setSubmitState((prev) => ({ ...prev, reviewDate: date }));
                }}
              />
              <SelectComment
                zIndex={zIndex}
                initial={submitState.comment}
                onResult={(text: string) => {
                  setSubmitState((prev) => ({ ...prev, comment: text }));
                }}
              />
            </FlexCol>
            <footer className="flex justify-between p-10 border-t shrink-0">
              <TGButton
                disabled={submitLoading}
                onClick={() => {
                  setOpenDrawerAudit(false);
                  clearSubmit();
                }}
              >
                Отмена
              </TGButton>
              <TGButton
                disabled={!isButtonDisabledMemoized || submitLoading}
                onClick={handleAuditSubmit}
                color="bluedark"
              >
                Отправить
              </TGButton>
            </footer>
          </DrawerComponent>
        ) : (
          <DrawerComponent zIndex={zIndex} open={openDrawerOWD} toggleDrawer={() => setOpenDrawerOWD((prev) => !prev)}>
            <header className="flex px-10 pt-10 pb-8 border-b shrink-0">
              <h1 className="text-black">Планирование OWD</h1>
            </header>
            <FlexCol className="divide-y grow">
              <SelectDocuments
                zIndex={zIndex}
                initial={submitState.documents}
                read={read?.documents}
                onResult={(items: Map<number, NotifyMyDocumentsType>) => {
                  setSubmitState((prev) => ({ ...prev, documents: items }));
                }}
              />

              <SelectEmployees
                zIndex={zIndex}
                title="Кто проводит OWD"
                initial={submitState.employees}
                onResult={(items: Map<number, NotifyMyEmployeesType>) => {
                  setSubmitState((prev) => ({ ...prev, employees: items }));
                }}
              />
              <SelectReviewDate
                zIndex={zIndex}
                title="Срок проведения OWD"
                modalTitle="Укажите срок проведения OWD"
                modalDescription="Срок проведения OWD"
                initial={submitState.reviewDate}
                onResult={(date: Date) => {
                  setSubmitState((prev) => ({ ...prev, reviewDate: date }));
                }}
              />
              <SelectComment
                zIndex={zIndex}
                initial={submitState.comment}
                onResult={(text: string) => {
                  setSubmitState((prev) => ({ ...prev, comment: text }));
                }}
              />
            </FlexCol>
            <footer className="flex justify-between p-10 border-t shrink-0">
              <TGButton
                disabled={submitLoading}
                onClick={() => {
                  setOpenDrawerOWD(false);
                  clearSubmit();
                }}
              >
                Отмена
              </TGButton>
              <TGButton
                disabled={!isButtonDisabledMemoized || submitLoading}
                onClick={handleOWDSubmit}
                color="bluedark"
              >
                Отправить
              </TGButton>
            </footer>
          </DrawerComponent>
        )}
      </Modal>
      <Modal
        zIndex={zIndex ? zIndex + 10 : 70}
        modalClassName="min-h-px"
        open={openSubmitNotificationModal}
        toggleDialog={toggleSubmitNotificationModal}
        autoClose={autoCloseModal}
        onAutoClose={() => {
          closeAllModals();
        }}
      >
        <header className="px-10 pb-4">
          <h2>Отправка уведомления</h2>
        </header>

        <div className="px-10 py-5">
          <p>Уведомления были успешно отправлены.</p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton color="bluedark" onClick={closeAllModals}>
            Закрыть
          </TGButton>
        </footer>
      </Modal>
      <Modal
        zIndex={100}
        modalClassName="min-h-0 min-w-min text-center"
        open={submitLoading}
        toggleDialog={() => false}
        hideXMarkIcon={true}
      >
        <header className="px-10 pb-4">
          <h2>Идет сохранение данных</h2>
        </header>

        <div className="justify-center items-center px-10 space-y-5">
          <p className="text-lg">Пожалуйста подождите...</p>
          <Loading />
        </div>
      </Modal>
    </>
  );
}

function ChooseTypeOfNotifyComponent({ type, onChange }: { type: NotifyType; onChange(type: NotifyType): void }) {
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    onChange(event.currentTarget.name as NotifyType);
  }

  return (
    <section className="px-10 py-5">
      <h4 className="mb-5 text-black">Выберите тип уведомления</h4>
      <div className="flex flex-nowrap gap-x-5">
        <button
          name="audit"
          onClick={handleClick}
          className={twMerge(
            type === "audit" ? "border-darkblue" : "border-[#9CA3AF]",
            "w-full p-5 rounded-lg flex flex-nowrap text-start gap-x-2.5 border",
          )}
        >
          <div>
            <EyeIcon width={24} height={24} color={type === "audit" ? "#012169" : "#9CA3AF"} />
          </div>
          <div>
            <div
              className={twMerge(type === "audit" ? "text-darkblue" : "text-[#637381]", "mb-2 text-[14px] font-medium")}
            >
              Ознакомление
            </div>
            <p className="text-[#8899A8] text-[12px]">Сотрудники должны ознакомиться c документом</p>
          </div>
        </button>
        <button
          name="owd"
          onClick={handleClick}
          className={twMerge(
            type === "owd" ? "border-darkblue" : "border-[#9CA3AF]",
            "w-full p-5 rounded-lg flex flex-nowrap text-start gap-x-2.5 border",
          )}
        >
          <div>
            <StarIcon width={24} height={24} color={type === "owd" ? "#012169" : "#9CA3AF"} />
          </div>
          <div>
            <div
              className={twMerge(type === "owd" ? "text-darkblue" : "text-[#637381]", "mb-2 text-[14px] font-medium")}
            >
              OWD
            </div>
            <p className="text-[#8899A8] text-[12px]">Сотрудники должны провести OWD</p>
          </div>
        </button>
      </div>
    </section>
  );
}
